<script setup>
import { watch, computed, ref } from 'vue';

import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import ngeohash from 'ngeohash';

import { storeToRefs } from 'pinia';

import Card from 'primevue/card';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import SelectButton from 'primevue/selectbutton';
import Skeleton from 'primevue/skeleton';

import useTrackingLinksStore from '@/stores/tracking-links';

import { MAPBOX_TOKEN } from '@/config/appConfig';

const props = defineProps({
  dataset: {
    type: String,
    default: 'total', // total | unique
  },
});

const title = `${props.dataset === 'total' ? 'Total' : 'Unique'} Visits by Location`;

const {
  isFetching,
  analytics,
} = storeToRefs(useTrackingLinksStore());

const mapContainer = ref(null);
const map = ref(null);

const center = ref([-98, 38]); // Center on America

/**
 * Draw the points and data onto the provided Mapbox map.
 */
const renderMapPoints = () => {
  const byLocation = analytics.value?.byLocation || [];

  map.value.addSource('points', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: byLocation.map((loc) => {
        const coordinates = ngeohash.decode(loc.key);

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [coordinates.longitude, coordinates.latitude],
          },
          properties: {
            id: loc.key,
            count: props.dataset === 'total' ? loc.doc_count : loc.unique_count,
          },
        };
      }),
    },
  });

  // Add a circle layer
  map.value.addLayer({
    id: 'circles',
    type: 'circle',
    source: 'points',
    paint: {
      'circle-radius': [
        'interpolate', ['linear'], ['get', 'count'],
        0, 10, // Min count = 10px radius
        50, 30, // Max count = 30px radius
      ],
      'circle-color': props.dataset === 'total' ? '#00B4CC' : '#B90071',
      'circle-opacity': 0.8,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#000000',
    },
  });

  // Add labels (numbers) inside circles
  map.value.addLayer({
    id: 'labels',
    type: 'symbol',
    source: 'points',
    layout: {
      'text-field': ['get', 'count'], // Display the 'count' property
      'text-size': 12,
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': '#000000',
    },
  });
};

/**
 * Initialize and render Mapbox component.
 */
const initializeMapbox = () => {
  mapboxgl.accessToken = MAPBOX_TOKEN; // Replace with your token

  map.value = new mapboxgl.Map({
    container: mapContainer.value,
    style: 'mapbox://styles/mapbox/streets-v11',
    center: center.value,
    zoom: 2,
    // Disable default UI controls
    attributionControl: false,
    interactive: true,
  });

  // Add geocoder (optional)
  const geocoder = new MapboxGeocoder({
    accessToken: mapboxgl.accessToken,
    mapboxgl,
  });

  map.value.addControl(geocoder);

  // Once the map loads, render H3 hexagons
  map.value.on('load', () => {
    renderMapPoints();
  });
};

watch([isFetching, mapContainer], () => {
  if (isFetching.value) {
    // Clear any maps already rendered
    if (mapContainer.value) {
      mapContainer.value.innerHTML = '';
    }

    return;
  }

  if (mapContainer.value) {
    initializeMapbox();
  }
});

const view = ref('map');
const viewOptions = ref([{
  icon: 'fas fa-map-location-dot',
  value: 'map',
  tooltip: 'Map View',
}, {
  icon: 'far fa-table-layout',
  value: 'table',
  tooltip: 'Table View',
}]);

const tableData = computed(() => {
  // Format location data to pretty city names by parsing key.
  return (analytics.value?.byCity || []).map((loc) => {
    const locationName = loc.key.join(', ');

    return {
      location: locationName,
      totalVisits: loc.doc_count,
      uniqueVisits: loc.unique_count,
    };
  });
});
</script>

<template>
  <Card
    pt:root:class="analytics-card analytics-card__visits-map shadow-2 relative"
    pt:body:class="w-full"
  >
    <template #content>
      <div v-if="isFetching" class="flex flex-column justify-content-center align-items-center">
        <Skeleton height="1rem" width="6rem" class="mb-2" />
        <Skeleton height="4rem" width="4rem" />
      </div>

      <Transition name="fade-in-out">
        <div v-if="!isFetching">
          <SelectButton
            class="analytics-card__view-type-toggle"
            v-model="view"
            :options="viewOptions"
            option-label="value"
            option-value="value"
            aria-labelledby="custom"
            data-entity="analytics-trackingLink-overTimeViewSwitcher"
            data-test="analytics-trackingLink-overTimeViewSwitcher"
          >
            <template #option="slotProps">
              <font-awesome-icon
                :icon="slotProps.option.icon"
                v-tooltip="slotProps.option.tooltip"
              />
            </template>
          </SelectButton>

          <h5 class="text-center p-0 m-0 mb-3">
            {{ title }}
          </h5>

          <div
            v-if="view === 'map'"
            ref="mapContainer"
            style="height: 300px;"
          />

          <DataTable
            v-else
            :value="tableData"
            pt:wrapper:class="shadow-none"
            aria-label="Visits by Location"
            scrollHeight="300px"
            data-test="analytics-trackingLink-visitsByLocation"
            data-entity="analytics-trackingLink-visitsByLocation"
          >
            <Column field="location" header="Location" />

            <Column
              field="totalVisits"
              header="Total Visits"
              class="analytics-card__table-column-small"
            />

            <Column
              field="uniqueVisits"
              header="Unique Visits"
              class="analytics-card__table-column-small"
            />
          </DataTable>
        </div>
      </Transition>
    </template>
  </Card>
</template>
